import { useState } from "react";
import { Link } from "react-router-dom";
import { Icon, familyMeta } from "./Icons.jsx";
import HeroVisual from "./HeroVisual.jsx";
import { techniques, techniquesById, moodToTechnique } from "../data/techniques.js";

export function Hero() {
  const chips = [
    "Based on Vigyan Bhairava Tantra",
    "112 Meditation Techniques",
    "Beginner-Friendly",
    "Modern-Life Practices",
    "Free Guided Paths",
  ];
  return (
    <header className="hero">
      <div className="hero-bg" />
      <div className="container hero-grid">
        <div>
          <div className="hero-eyebrow">VIGYAN BHAIRAVA TANTRA · 112 MEDITATION DOORWAYS</div>
          <h1>112 Ancient Doorways<br />for the <em>Modern Mind</em></h1>
          <p className="hero-body">
            The Vigyan Bhairava Tantra offers 112 ways to enter meditation — through breath,
            sound, body, emotion, silence, love, fear, space, and awareness. Simple Meditation
            World translates them into simple practices for today's restless life.
          </p>
          <div className="hero-ctas">
            <Link className="btn btn-primary" to="/find-my-doorway">Find My Technique {Icon.arrowSm()}</Link>
            <Link className="btn btn-secondary" to="/techniques">Explore All 112</Link>
            <Link className="btn btn-ghost" to="/beginner-path">Start 7-Day Beginner Path →</Link>
          </div>
          <div className="hero-chips">
            {chips.map((c) => (
              <span key={c} className="chip"><span className="chip-dot" /> {c}</span>
            ))}
          </div>
        </div>
        <div className="hero-visual"><HeroVisual /></div>
      </div>
    </header>
  );
}

export function TodaysDoorway() {
  const tech = techniquesById[94];
  const fam = familyMeta[tech.family];
  const today = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return (
    <section className="today-strip" style={{ padding: "36px 0" }} id="today">
      <div className="container today-inner">
        <div className="today-pill"><span className="pulse" /> Today's Doorway · {today}</div>
        <div className="today-text">
          <div className="today-title">{tech.title}</div>
          <div className="today-body">{tech.modern}</div>
          <div className="today-meta">
            <span>{fam.icon(14)} {fam.label} Doorway</span>
            <span className="dot"></span>
            <span>{tech.time} min</span>
            <span className="dot"></span>
            <span>Technique {tech.id} · Verse {tech.sourceVerse}</span>
          </div>
        </div>
        <Link className="btn btn-primary" to={`/technique/${tech.id}`} style={{ alignSelf: "center" }}>
          Try Today's Practice {Icon.arrowSm()}
        </Link>
      </div>
    </section>
  );
}

export function WhyDifferent() {
  const cards = [
    { num: "01", icon: Icon.breath(28), title: "Breath", body: "For overthinking, anxiety, and focus." },
    { num: "02", icon: Icon.sound(28), title: "Sound", body: "For people who feel silence through vibration." },
    { num: "03", icon: Icon.emotion(28), title: "Emotion", body: "For desire, anger, jealousy, pleasure, pain, and joy." },
    { num: "04", icon: Icon.daily(28), title: "Daily Life", body: "For ordinary desire, eating, movement, senses, thought, and identity." },
  ];
  return (
    <section className="section-ivory" id="why">
      <div className="container">
        <div className="divider-glyph" aria-hidden="true">· · ·</div>
        <div className="section-head">
          <span className="eyebrow">A different premise</span>
          <h2>Meditation is not one method.<br /><em>It is 112 doorways.</em></h2>
          <p>Some people enter through breath. Some through sound. Some through love. Some through silence. Some through fear, beauty, darkness, or the tiny gap between two thoughts.</p>
        </div>
        <div className="why-grid">
          {cards.map((c) => (
            <div key={c.num} className="door-card">
              <span className="door-num">{c.num} / 04</span>
              <div className="icon">{c.icon}</div>
              <h3>{c.title}</h3>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ChooseByProblem() {
  const problems = [
    { ic: Icon.brain(20), title: "Stop overthinking", anchor: "restless" },
    { ic: Icon.phone(20), title: "Meet phone craving", anchor: "phone" },
    { ic: Icon.moon(20), title: "Sleep peacefully", anchor: "sleep" },
    { ic: Icon.flame(20), title: "Handle anger", anchor: "angry" },
    { ic: Icon.fear(20), title: "Calm anxiety", anchor: "fearful" },
    { ic: Icon.heart(20), title: "Heal loneliness", anchor: "lonely" },
    { ic: Icon.focus(20), title: "Improve focus", anchor: "focus" },
    { ic: Icon.spark(20), title: "Go deeper spiritually", anchor: "curious" },
  ];
  return (
    <section className="section-ivory" id="problems" style={{ background: "#FBF5EA" }}>
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">A practical map</span>
          <h2>What do you need <em>help with today?</em></h2>
          <p>Eight modern problems. Each one opens a curated set of techniques from the 112.</p>
        </div>
        <div className="problem-grid">
          {problems.map((p) => {
            const techId = moodToTechnique[p.anchor];
            const count = techniques.filter((t) => t.best.join(" ").toLowerCase().includes(p.title.split(" ").slice(-1)[0].toLowerCase())).length;
            return (
              <Link key={p.title} className="problem-card" to={`/find-my-doorway?mood=${p.anchor}`}>
                <div className="p-ic">{p.ic}</div>
                <div className="p-title">{p.title}</div>
                <div className="p-count">
                  <span>Start with Technique {techId}</span>
                  <span className="arr">{Icon.arrowSm()}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function FindMyDoorwayBlock() {
  const moods = [
    { id: "restless", label: "Restless / Overthinking", icon: Icon.brain(22) },
    { id: "phone", label: "Phone Craving", icon: Icon.phone(22) },
    { id: "angry", label: "Angry / Emotionally Charged", icon: Icon.flame(22) },
    { id: "fearful", label: "Fearful / Anxious", icon: Icon.fear(22) },
    { id: "tired", label: "Tired / Low Energy", icon: Icon.battery(22) },
    { id: "sleep", label: "Cannot Sleep", icon: Icon.moon(22) },
    { id: "lonely", label: "Heartbroken / Lonely", icon: Icon.heart(22) },
    { id: "focus", label: "Need Focus", icon: Icon.focus(22) },
    { id: "curious", label: "Spiritually Curious", icon: Icon.spark(22) },
    { id: "silent", label: "Want Silence", icon: Icon.silence(22) },
  ];
  const times = ["1 minute", "5 minutes", "12 minutes", "21 minutes", "Deep practice"];
  const [mood, setMood] = useState(null);
  const [time, setTime] = useState("5 minutes");
  const tech = mood ? techniquesById[moodToTechnique[mood]] : null;

  return (
    <section className="finder" id="find">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow" style={{ color: "var(--gold)" }}>Find your doorway</span>
          <h2 style={{ color: "var(--ivory)" }}>What is your mind like <em>today?</em></h2>
          <p style={{ color: "rgba(248, 241, 231, 0.7)" }}>One honest answer reveals the doorway that fits.</p>
        </div>
        <div className="finder-wrap">
          <div className="finder-step"><span className="finder-step-num">1</span>How does your mind feel right now?</div>
          <div className="mood-grid" role="radiogroup" aria-label="Mood">
            {moods.map((m) => (
              <button key={m.id} role="radio" aria-checked={mood === m.id}
                className={`mood-card ${mood === m.id ? "selected" : ""}`}
                onClick={() => setMood(m.id)}>
                <div className="mood-icon">{m.icon}</div>
                <div className="mood-label">{m.label}</div>
              </button>
            ))}
          </div>
          <div className="finder-step"><span className="finder-step-num">2</span>How much time do you have?</div>
          <div className="time-row" role="radiogroup" aria-label="Time">
            {times.map((t) => (
              <button key={t} role="radio" aria-checked={time === t}
                className={`time-pill ${time === t ? "selected" : ""}`} onClick={() => setTime(t)}>{t}</button>
            ))}
          </div>
          <div className="finder-cta-row">
            <div className="finder-status">
              {mood ? <>You chose <strong>{moods.find((m) => m.id === mood).label}</strong> · <strong>{time}</strong></>
                    : <>Pick a state and a duration — we'll match you to one of the 112 techniques.</>}
            </div>
          </div>
          {tech && (
            <div className="result-card">
              <div className="result-eyebrow">Your doorway today</div>
              <div className="result-grid">
                <div className="result-bignum"><small>Technique</small>{String(tech.id).padStart(2, "0")}</div>
                <div>
                  <div className="result-title">{tech.title}</div>
                  <div className="result-meta">
                    <span><b>Best for:</b> {tech.best.slice(0, 3).join(" · ")}</span>
                    <span className="sep">|</span>
                    <span><b>Try now:</b> {tech.time} min</span>
                  </div>
                  <div className="result-why"><b>Why this fits ·</b> {tech.modern}</div>
                  <div className="audio-chips">
                    <span className="audio-chip">📖 Read · 2 min</span>
                    <span className="audio-chip">◐ Practice · {tech.time} min</span>
                    <span className="audio-chip soon">♪ Listen · 5 min guided <span className="badge">Soon</span></span>
                  </div>
                  <div className="result-actions">
                    <Link className="btn btn-primary" to={`/technique/${tech.id}`}>Start Practice {Icon.arrowSm()}</Link>
                    <button className="btn-ghost-light" onClick={() => setMood(null)}>↻ Try Another Doorway</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export function ModernLife() {
  const items = [
    { id: 73, tag: "CRAVING / PHONE URGE", title: "Before the Urge Takes Over" },
    { id: 38, tag: "OVERTHINKING", title: "Between Two Thoughts" },
    { id: 78, tag: "ANGER / JEALOUSY", title: "Strong Emotion, Stop the Mind" },
    { id: 52, tag: "SLEEP", title: "At the Threshold of Sleep" },
    { id: 94, tag: "SENSE OVERLOAD", title: "When a Sense Becomes Vivid" },
    { id: 61, tag: "SPACIOUSNESS", title: "Look at the Clear Sky" },
  ];
  return (
    <section className="section-ivory" id="modern">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">For modern life</span>
          <h2>Ancient techniques <em>for modern problems</em></h2>
          <p>Small practices for the situations that actually fill your day.</p>
        </div>
        <div className="mlife-grid">
          {items.map((it) => {
            const tech = techniquesById[it.id];
            return (
              <div key={it.id} className="mlife-card">
                <span className="mlife-tag">{it.tag}</span>
                <h3>{it.title}</h3>
                <p>{tech.modern}</p>
                <Link className="try" to={`/technique/${tech.id}`}>Try this technique {Icon.arrowSm()}</Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function ExploreFamilies() {
  const order = ["breath", "sound", "body", "emotion", "space", "sleep", "love", "daily"];
  return (
    <section className="section-night subtle" id="explore">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow" style={{ color: "var(--gold)" }}>The map</span>
          <h2>Explore the 112 <em>by doorway</em></h2>
          <p>The 112 techniques cluster into eight families. Each is a different way in.</p>
        </div>
        <div className="fam-grid">
          {order.map((key, i) => {
            const f = familyMeta[key];
            return (
              <Link key={key} to={`/techniques?family=${key}`} className="fam-card">
                <div className="fam-num">FAMILY {String(i + 1).padStart(2, "0")} <span style={{ marginLeft: 8, color: "var(--gold)" }}>{f.icon(20)}</span></div>
                <h3>{f.label} Doorways</h3>
                <p>{f.blurb}</p>
                <div className="fam-count">
                  <span>{f.count} techniques</span>
                  <span className="arrow">{Icon.arrowSm()}</span>
                </div>
              </Link>
            );
          })}
        </div>
        <div style={{ textAlign: "center", marginTop: 50 }}>
          <Link className="btn btn-primary" to="/techniques">Explore All 112 Techniques {Icon.arrowSm()}</Link>
        </div>
      </div>
    </section>
  );
}

export function FeaturedTechniquePreview() {
  const tech = techniquesById[1];
  return (
    <section className="section-ivory" id="technique-preview">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Format you can actually use</span>
          <h2>Every technique explained <em>for real life</em></h2>
          <p>Ancient essence on one side. A clear modern practice on the other.</p>
        </div>
        <div className="featured-wrap">
          <div className="featured-left">
            <div className="tech-num">TECHNIQUE 01 · BREATH DOORWAY</div>
            <h3 className="tech-title">{tech.title}</h3>
            <div className="tech-block"><div className="lbl">Ancient Essence</div><p>"{tech.essence}"</p></div>
            <div className="tech-block modern"><div className="lbl">Modern Translation</div><p>{tech.modern}</p></div>
            <div className="tech-block">
              <div className="lbl">Best For</div>
              <div className="tech-tags" style={{ marginTop: 8 }}>
                {tech.best.map((t) => <span key={t} className="tech-tag">{t}</span>)}
              </div>
            </div>
          </div>
          <div className="featured-right">
            <h4>Try it now · {tech.time} minutes</h4>
            <ol className="steps">{tech.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
            <div className="warn"><b>Common mistake →</b> {tech.mistake}</div>
            <div style={{ marginTop: 28, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Link className="btn btn-primary" to={`/technique/${tech.id}`}>Open Full Technique {Icon.arrowSm()}</Link>
              <Link className="btn btn-secondary" to="/techniques">See All 112</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function PopularDoorways() {
  const items = [
    { id: 1, rank: "01", trend: "Start with breath" },
    { id: 16, rank: "02", trend: "Start with sound" },
    { id: 36, rank: "03", trend: "Start with space" },
    { id: 38, rank: "04", trend: "For overthinking" },
    { id: 52, rank: "05", trend: "For sleep" },
    { id: 73, rank: "06", trend: "For craving" },
  ];
  return (
    <section className="section-ivory" id="popular">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Suggested starting points</span>
          <h2>Begin with these <em>doorways</em></h2>
        </div>
        <div className="popular-grid">
          {items.map((it) => {
            const tech = techniquesById[it.id];
            return (
              <Link key={it.id} className="popular-card" to={`/technique/${tech.id}`}>
                <div className="trend"><span className="trend-num">{it.rank}</span> {it.trend}</div>
                <h3>{tech.title}</h3>
                <div className="pop-tech">Technique {tech.id} · {familyMeta[tech.family].label} Doorway</div>
                <div className="pop-meta">
                  <span><b>{tech.time} min</b></span>
                  <span className="dt"></span>
                  <span>Source verse {tech.sourceVerse}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function ChooseDepth() {
  return (
    <section className="section-ivory" id="depth">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Two starting points</span>
          <h2>Choose <em>your depth</em></h2>
          <p>Whether you've never sat in silence or you've been practicing for years.</p>
        </div>
        <div className="depth-wrap">
          <div className="depth-card beginner">
            <div className="d-eyebrow">Start here</div>
            <h3>I am new<br /> to meditation</h3>
            <p>Begin with the gentlest techniques — breath, body, sound, and daily-life doorways. No belief required.</p>
            <div className="depth-tags">
              <span className="depth-tag">Breath</span><span className="depth-tag">Body</span>
              <span className="depth-tag">Sound</span><span className="depth-tag">Daily Life</span>
            </div>
            <Link className="btn btn-primary" to="/beginner-path">Start the 7-Day Path {Icon.arrowSm()}</Link>
          </div>
          <div className="depth-card deep">
            <div className="d-eyebrow">Go deeper</div>
            <h3>I have practiced<br /> before</h3>
            <p>Explore the subtler doorways — emotion, space, void, devotion, and awareness.</p>
            <div className="depth-tags">
              <span className="depth-tag">Emotion</span><span className="depth-tag">Space</span>
              <span className="depth-tag">Void</span><span className="depth-tag">Devotion</span>
            </div>
            <Link className="btn btn-primary" to="/techniques?level=3">Explore Deeper Doorways {Icon.arrowSm()}</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function PresentGeneration() {
  const cards = [
    { ic: Icon.student(22), t: "For students" },
    { ic: Icon.brief(22), t: "For working professionals" },
    { ic: Icon.parents(22), t: "For parents" },
    { ic: Icon.creator(22), t: "For creators" },
    { ic: Icon.brain(22), t: "For overthinkers" },
    { ic: Icon.seek(22), t: "For spiritual seekers" },
    { ic: Icon.scroll(22), t: "For people addicted to scrolling" },
    { ic: Icon.still(22), t: "For people who cannot sit still" },
  ];
  return (
    <section className="section-ivory" id="generation">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">For the present generation</span>
          <h2>Built for the <em>distracted generation</em></h2>
          <p>You do not need a cave. You do not need hours. You need one honest doorway into awareness — while living your real life.</p>
        </div>
        <div className="gen-grid">
          {cards.map((c) => (
            <div key={c.t} className="gen-card">
              <div className="gen-ic">{c.ic}</div>
              <div className="gen-text">{c.t}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PracticeGently() {
  return (
    <section className="section-ivory" style={{ padding: "60px 0" }} id="gentle">
      <div className="container">
        <div className="gentle-note">
          <div className="gn-ic" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v5M12 16.5v.1" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <h4>Practice gently.</h4>
            <p>Do not force breath, emotion, fear, or altered states. Stop if you feel overwhelmed. These practices are for meditation education and self-awareness — not medical or mental-health treatment. If you're struggling, please reach out to a qualified professional.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FinalCTA() {
  return (
    <section className="final-cta">
      <div className="container">
        <div className="divider-glyph" style={{ marginBottom: 24, color: "var(--gold)", opacity: 0.7 }} aria-hidden="true">· ॐ ·</div>
        <h2>Find the doorway that fits<br /> your mind <em>today.</em></h2>
        <p>Start with one technique. Try it honestly. Let direct experience teach you.</p>
        <div className="final-cta-row">
          <Link className="btn btn-primary" to="/find-my-doorway">Find My Technique {Icon.arrowSm()}</Link>
          <Link className="btn btn-secondary" to="/techniques">Explore All 112</Link>
          <Link className="btn btn-ghost" to="/beginner-path">Start 7-Day Path →</Link>
        </div>
      </div>
    </section>
  );
}
